/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import { groupBy, debounce } from 'lodash';
import idx from 'idx';
import invariant from 'invariant';

import { rootGroupCode } from 'entities/RootGroup';
import { withRouter } from 'app/context/Router';
import ChatActions from 'chat/actions/ChatActions';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import Glyph from 'components/Glyph';
import Group from 'entities/Group';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import ScrollContainer from 'components/ScrollContainer';
import VisibilityWatch from 'components/VisibilityWatch';
import SearchBlock from 'components/SearchBlock';
import StreamSectionListBroadcasts from './StreamSectionListBroadcasts';
import StreamSectionListChats from './StreamSectionListChats';
import StreamSectionListPeople from './StreamSectionListPeople';
import StreamSectionListLoadTimer from './StreamSectionListLoadTimer';
import TriggerComposerButton from 'chat/components/TriggerComposerButton';
import CLASS_STREAM_SECTIONS_QUERY from '../queries/ClassStreamSectionsQuery.gql';
import ORG_STREAM_SECTIONS_QUERY from '../queries/OrgStreamSectionsQuery.gql';

import type { ClassStreamSections } from '../queries/__generated__/ClassStreamSections';
import type { OrgStreamSections } from '../queries/__generated__/OrgStreamSections';

import 'chat/components/styles/ChatList.scss';

export const TOUCH_KEY = 'STREAM_SECTION_LIST';

export class SectionHeader extends React.Component<{ text: string }> {
  render() {
    return (
      <Heading level="2" levelStyle="3" divided>
        {this.props.text}
      </Heading>
    );
  }
}

const isRootGroup = (group: Group) => group.getCode() === rootGroupCode;

const shouldFetchClassStreamSections = (group: Group) =>
  group.getType() !== 'district' || !group.getOrganization();

const shouldFetchOrgStreamSections = (group: Group) => group.getType() === 'district';

type Props = {
  router: *,
  classData?: ReactApolloData<ClassStreamSections>,
  organizationData?: ReactApolloData<OrgStreamSections>,
  group: Group,
  touch: any,
};

type State = {
  scrolledToBottom: boolean,
  searchQuery: string,
};

class StreamSectionList extends React.Component<Props, State> {
  state = {
    scrolledToBottom: false,
    searchQuery: '',
  };

  componentDidMount() {
    ChatActions.loadPotentialChatMembers('');
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.touch !== this.props.touch) {
      this.handleTouch();
    }
  }

  handleTouch() {
    if (isRootGroup(this.props.group)) {
      return;
    }

    if (shouldFetchClassStreamSections(this.props.group) && this.props.classData) {
      this.props.classData.refetch();
    }

    if (shouldFetchOrgStreamSections(this.props.group) && this.props.organizationData) {
      this.props.organizationData.refetch();
    }
  }

  _searchBlock: { handleClose: () => void };

  setScrolledToBottom = debounce(
    (scrolledToBottom: boolean) => {
      this.setState({ scrolledToBottom });
    },
    200,
    { leading: true, trailing: true }
  );

  handleQueryChange = debounce(
    (rawQuery: string = '') => {
      const query = rawQuery.trim();
      this.setState({ searchQuery: query });
    },
    800,
    { leading: true, trailing: true }
  );

  getMemberIdFromParams = () => {
    const { params } = this.props.router;
    return params ? params.memberId : null;
  };

  clearSearchQuery = () => {
    this.setState({ searchQuery: '' });
    this._searchBlock.handleClose();
  };

  isEntityStream = (stream: *) => {
    return stream.__typename === 'EntityStream';
  };

  isChatStream = (stream: *) => {
    return stream.__typename === 'ChatStream';
  };

  renderVisibilityWatcher() {
    return this.state.searchQuery ? null : (
      <VisibilityWatch
        whileVisible={() => this.setScrolledToBottom(true)}
        whileInvisible={() => this.setScrolledToBottom(false)}
        interval={500}>
        <div />
      </VisibilityWatch>
    );
  }

  renderBroadcasts = (streamSection: *) => {
    const { searchQuery } = this.state;
    if (searchQuery) {
      return null;
    }
    const { streams } = streamSection;
    if (!streams) {
      return (
        <ErrorReporter error="cannot pass a streamSection without streams into renderBroadcasts" />
      );
    }
    const canSendToAnEntityStream =
      streams.length > 0 &&
      streams.find(
        // $FlowIssue flow doesn't recognize the check being done in isEntityStream
        stream => this.isEntityStream(stream) && stream.permissions && stream.permissions.canSend
      );
    if (!canSendToAnEntityStream) {
      return null;
    }
    return (
      <div>
        <SectionHeader text="Announcements" />
        <Row size="small">
          <StreamSectionListBroadcasts
            streams={streams}
            groupCode={this.props.group.getCodeOrError()}
            group={this.props.group}
          />
        </Row>
      </div>
    );
  };

  renderPeople() {
    const { searchQuery } = this.state;
    if (!searchQuery) {
      return null;
    }
    return (
      <div>
        <StreamSectionListPeople
          group={this.props.group}
          searchQuery={searchQuery}
          onSelect={this.clearSearchQuery}
        />
      </div>
    );
  }

  renderChats = (
    streamSection: *,
    groupId: Backbone$Id,
    groupCode: string,
    newChatMemberId: ?string
  ) => {
    const { title, streams } = streamSection;
    const groupedStreams = streams ? groupBy(streams, '__typename') : {};
    const entityChatUuids = (groupedStreams.EntityStream || []).map(stream => stream.uuid);
    const chatStreamUuids = (groupedStreams.ChatStream || []).map(stream => stream.uuid);
    return (
      <div>
        <SectionHeader text={title} />
        <Row size="small">
          <StreamSectionListChats
            groupId={groupId}
            groupCode={groupCode}
            isScrolledToBottom={this.state.scrolledToBottom}
            searchQuery={this.state.searchQuery}
            entityChatUuids={entityChatUuids}
            chatStreamUuids={chatStreamUuids}
            newChatMemberId={newChatMemberId}
          />
        </Row>
      </div>
    );
  };

  renderHeader = () => {
    return (
      <div ref="header" className="ChatList-header">
        <MediaObject center figure="right" gutter="small" className="ChatList-header-title">
          <SearchBlock
            ref={ref => {
              if (ref && ref.handleClose) {
                this._searchBlock = ref;
              }
            }}
            className="ChatList-searchBlock"
            label="Search for people to message"
            onDismiss={this.handleQueryChange}
            onQueryChange={this.handleQueryChange}
            data-test-selector="chatlist_search_input"
          />
          <TriggerComposerButton
            label={
              <Glyph
                name="pencil-compose-stroke"
                solo
                className="ChatList-header-search-button-icon"
              />
            }
            size="xsmall"
          />
        </MediaObject>
      </div>
    );
  };

  renderContent = (classOrOrg: *) => {
    invariant(
      classOrOrg,
      'cannot render StreamSectionList content without a class or an organization'
    );
    const { group } = this.props;
    const { streamSections } = classOrOrg;
    const broadcastStreamSection = streamSections
      ? streamSections.find(s => s.type === 'broadcasts')
      : null;
    const chatStreamSection = streamSections ? streamSections.find(s => s.type === 'chats') : null;

    return (
      <div className="ChatList GroupMessages-sidebar">
        <StreamSectionListLoadTimer groupUuid={group.getUuid()} />
        {this.renderHeader()}
        <ScrollContainer ref="scrollArea">
          <ul className="ChatList-list">
            {broadcastStreamSection && idx(broadcastStreamSection, _ => _.streams.length)
              ? this.renderBroadcasts(broadcastStreamSection)
              : null}
            {chatStreamSection &&
              this.renderChats(
                chatStreamSection,
                group.id,
                group.getCodeOrError(),
                this.getMemberIdFromParams()
              )}
            {this.renderPeople()}
            {this.renderVisibilityWatcher()}
          </ul>
        </ScrollContainer>
      </div>
    );
  };

  render() {
    /* For root group */
    const { group } = this.props;
    if (group.getCode() === rootGroupCode) {
      return (
        <div className="ChatList GroupMessages-sidebar">
          {this.renderHeader()}
          <ScrollContainer ref="scrollArea">
            <ul className="ChatList-list">
              {this.renderChats(
                { title: 'Conversations', streams: null },
                -1,
                rootGroupCode,
                this.getMemberIdFromParams()
              )}
              {this.renderVisibilityWatcher()}
            </ul>
          </ScrollContainer>
        </div>
      );
    }

    /* For all other classes and orgs */
    const { classData, organizationData } = this.props;
    if (!(classData || organizationData)) {
      return <ErrorReporter error="StreamSectionList tried to render without data" />;
    }

    if (
      (classData && classData.loading && !idx(classData, _ => _.classes[0])) ||
      (organizationData &&
        organizationData.loading &&
        !idx(organizationData, _ => _.organizations[0]))
    ) {
      return (
        <div className="ChatList GroupMessages-sidebar">
          {this.renderHeader()}
          <ScrollContainer ref="scrollArea">
            <ul className="ChatList-list">
              <CenteredLoadingIndicator />
            </ul>
          </ScrollContainer>
        </div>
      );
    }

    if (classData && idx(classData, _ => _.classes[0])) {
      return this.renderContent(classData.classes[0]);
    } else if (organizationData && idx(organizationData, _ => _.organizations[0])) {
      return this.renderContent(organizationData.organizations[0]);
    }
    return null;
  }
}

const withClass = graphql(CLASS_STREAM_SECTIONS_QUERY, {
  options: ({ group }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      classUuid: group.getUuid(),
    },
  }),
  props: ({ data }) => {
    return { classData: data };
  },
  skip: ({ group }) => shouldFetchOrgStreamSections(group) || isRootGroup(group),
});

const withOrganization = graphql(ORG_STREAM_SECTIONS_QUERY, {
  options: ({ group }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      orgUuid: group.getOrganization().get('uuid'),
    },
  }),
  props: ({ data }) => {
    return { organizationData: data };
  },
  skip: ({ group }) => shouldFetchClassStreamSections(group) || isRootGroup(group),
});

function mapStateToProps(state: Object) {
  return {
    touch: state.touch.get(TOUCH_KEY),
  };
}

export { StreamSectionList };

export default compose(
  connect(
    mapStateToProps,
    undefined
  ),
  withRouter,
  withClass,
  withOrganization
)(StreamSectionList);
